import SaveIcon from '@mui/icons-material/SaveRounded';
import { KgPermissions } from 'studio/constants';
import RestorePageIcon from '@mui/icons-material/RestorePageRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { fileTypes } from 'studio/pages/Editor/FileTypeConstants';
import SyncIcon from '@mui/icons-material/SyncRounded';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Paper from '@mui/material/Paper';
import React, { forwardRef, useContext } from 'react';
import { withStyles, withTheme } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import styles from 'studio/pages/Editor/Editor.styles';
import { EditorContext } from 'studio/pages/Editor/Editor';
import { replaceText } from 'studio/pages/Editor/EditorUtils';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

/** Component for an action button in the toolbar with icon and text */
const ActionButtonWithText = forwardRef(({ tooltip, icon, classes, onClick, text, disabled, ...props }, ref) => (
  <Tooltip title={disabled ? '' : tooltip} placement="bottom">
    <Button
      className={classes.actionButtonWithText}
      variant="outlined"
      color="primary"
      size="small"
      startIcon={icon}
      onClick={onClick}
      ref={ref}
      disabled={disabled}
      {...props}
    >
      {text}
    </Button>
  </Tooltip>
));

/** Toolbar for currently active file (shown on above the editor) */
const EditorFileToolbar = ({ classes, knowledgeGraph }) => {
  const {
    user,
    kgPermissions,
    editor,
    editorInstances,
    fileState: { activeFile, activeFilePath, activeFileContent },
    fileOperations: { saveFileContent },
    deletion: { setFilesToDelete },
    sync: { openSyncDialog, activeFileSynchronizableConcepts },
    openCopilotEditVklDialog,
    openComparisonDialog
  } = useContext(EditorContext);

  return (
    <Paper square elevation={2} className={classes.fileActionBar} data-test="editorFileToolbar">
      <ActionButtonWithText
        icon={<SaveIcon />}
        tooltip="Save changes"
        classes={classes}
        text={activeFileContent.isModified ? 'Save' : 'Saved'}
        onClick={saveFileContent}
        disabled={!activeFileContent.isModified || !kgPermissions.includes(KgPermissions.Write)}
        data-test="editorSaveFileButton"
        color="primary"
      />
      <ActionButtonWithText
        icon={<RestorePageIcon />}
        tooltip="Revert to last saved version"
        classes={classes}
        text="Discard"
        onClick={() => {
          replaceText(editorInstances[activeFilePath].model, activeFileContent.content);
          editor.focus();
        }}
        disabled={!activeFileContent.isModified || !kgPermissions.includes(KgPermissions.Write)}
        data-test="editorDiscardChangesButton"
      />
      <ActionButtonWithText
        icon={<DeleteIcon />}
        tooltip="Delete file"
        classes={classes}
        text="Delete"
        onClick={() => setFilesToDelete([activeFile])}
        data-test="editorDeleteFileButton"
        color="secondary"
        disabled={!kgPermissions.includes(KgPermissions.Write)}
      />

      {[fileTypes.class, fileTypes.entities, fileTypes.coOccurrenceDeclaration, fileTypes.coOccurrenceFacts].includes(
        activeFile.data.type
      ) && (
        <ActionButtonWithText
          icon={<SyncIcon />}
          tooltip="Synchronize entities from the database"
          classes={classes}
          text="Sync"
          onClick={() => openSyncDialog(activeFile.data.id)}
          disabled={!kgPermissions.includes(KgPermissions.KgSync) || !activeFileSynchronizableConcepts.length}
        />
      )}
      {user.isStudioCopilotEnabled && (
        <ActionButtonWithText
          icon={<AutoAwesomeRoundedIcon />}
          tooltip={
            'Edit the ' +
            (editor?.getModel()?.getValueInRange(editor.getSelection()) ? 'selected VKL' : 'VKL file') +
            ' with Studio Copilot (Ctrl+Shift+C)'
          }
          classes={classes}
          text="Copilot"
          onClick={openCopilotEditVklDialog}
        />
      )}
      {knowledgeGraph?.data?.developmentKgForName && (
        <ActionButtonWithText
          icon={<CompareArrowsIcon />}
          tooltip="Compares file with the state in main"
          classes={classes}
          text="Compare"
          onClick={openComparisonDialog}
        />
      )}
    </Paper>
  );
};

export default withTheme(withStyles(styles)(EditorFileToolbar));
